import { Head, router } from "@inertiajs/react"
import { AlertTriangle, CheckCircle, XCircle } from "lucide-react"
import { useState } from "react"

import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import AppLayout from "@/layouts/app-layout"
import { formatCurrency } from "@/lib/utils"
import { confirmCsvImportsPath, newCsvImportPath } from "@/routes"
import type { BreadcrumbItem, Category } from "@/types"

interface PreviewRow {
  index: number
  description: string
  amount_cents: number
  date: string
  direction: string
  status: string
  category_id: number | null
  category_name: string | null
  duplicate: boolean
}

interface PreviewProps {
  rows: PreviewRow[]
  total_rows: number
  headers: string[]
  column_mapping: Record<string, string>
  auto_mapping: Record<string, string>
  parse_errors: { row: number; reason: string }[]
  file_content: string
  filename: string
  default_direction: string
  categories: Category[]
}

const breadcrumbs: BreadcrumbItem[] = [
  { title: "Import CSV", href: newCsvImportPath() },
]

export default function Preview({
  rows,
  total_rows,
  headers,
  column_mapping: initialMapping,
  parse_errors,
  file_content,
  filename,
}: PreviewProps) {
  const [mapping, setMapping] =
    useState<Partial<Record<string, string>>>(initialMapping)
  const [sourceName, setSourceName] = useState("")
  const [submitting, setSubmitting] = useState(false)
  const [step, setStep] = useState<"mapping" | "preview">(
    initialMapping.description && initialMapping.amount && initialMapping.date
      ? "preview"
      : "mapping",
  )

  const categorizedCount = rows.filter((r) => r.status === "categorized").length
  const needsReviewCount = rows.filter(
    (r) => r.status === "needs_review" && !r.duplicate,
  ).length
  const duplicateCount = rows.filter((r) => r.duplicate).length

  function updateMapping(field: string, header: string) {
    setMapping((prev) => ({ ...prev, [field]: header }))
  }

  function handleConfirm() {
    setSubmitting(true)
    router.post(confirmCsvImportsPath(), {
      file_content,
      column_mapping: JSON.stringify(mapping),
      filename,
      source_name: sourceName,
    })
  }

  if (step === "mapping") {
    return (
      <AppLayout breadcrumbs={breadcrumbs}>
        <Head title="Map Columns" />

        <div className="mx-auto w-full max-w-2xl space-y-6 p-4">
          <div>
            <h2 className="text-lg font-semibold">Map Columns</h2>
            <p className="text-muted-foreground text-sm">
              Step 2 of 3: Tell us which columns contain your data
            </p>
          </div>

          <div className="space-y-4">
            {["description", "date"].map((field) => (
              <div key={field} className="grid gap-2">
                <Label className="capitalize">
                  {field} <span className="text-red-500">*</span>
                </Label>
                <Select
                  value={mapping[field] ?? ""}
                  onValueChange={(v) => updateMapping(field, v)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={`Select ${field} column`} />
                  </SelectTrigger>
                  <SelectContent>
                    {headers.map((h) => (
                      <SelectItem key={h} value={h}>
                        {h}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            ))}

            <div className="space-y-4 rounded-lg border p-4">
              <Label className="text-sm font-medium">Amount columns</Label>
              <p className="text-muted-foreground text-xs">
                Use a single Amount column, or separate Withdrawal/Deposit
                columns (common in bank exports).
              </p>

              <div className="grid gap-2">
                <Label className="text-xs">Amount (single column)</Label>
                <Select
                  value={mapping.amount ?? "__none__"}
                  onValueChange={(v) => {
                    const val = v === "__none__" ? undefined : v
                    if (val) {
                      setMapping((prev) => {
                        const next: Partial<Record<string, string>> = {
                          ...prev,
                          amount: val,
                        }
                        delete next.withdrawal
                        delete next.deposit
                        return next
                      })
                    } else {
                      setMapping((prev) => {
                        const next = { ...prev }
                        delete next.amount
                        return next
                      })
                    }
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select amount column" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">None</SelectItem>
                    {headers.map((h) => (
                      <SelectItem key={h} value={h}>
                        {h}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="text-muted-foreground text-center text-xs">
                or
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="grid gap-2">
                  <Label className="text-xs">Withdrawal / Debit</Label>
                  <Select
                    value={mapping.withdrawal ?? "__none__"}
                    onValueChange={(v) => {
                      const val = v === "__none__" ? undefined : v
                      setMapping((prev) => {
                        const next = { ...prev }
                        if (val) {
                          next.withdrawal = val
                          delete next.amount
                        } else {
                          delete next.withdrawal
                        }
                        return next
                      })
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select column" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">None</SelectItem>
                      {headers.map((h) => (
                        <SelectItem key={h} value={h}>
                          {h}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid gap-2">
                  <Label className="text-xs">Deposit / Credit</Label>
                  <Select
                    value={mapping.deposit ?? "__none__"}
                    onValueChange={(v) => {
                      const val = v === "__none__" ? undefined : v
                      setMapping((prev) => {
                        const next = { ...prev }
                        if (val) {
                          next.deposit = val
                          delete next.amount
                        } else {
                          delete next.deposit
                        }
                        return next
                      })
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select column" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">None</SelectItem>
                      {headers.map((h) => (
                        <SelectItem key={h} value={h}>
                          {h}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          </div>

          <div className="flex justify-end">
            <Button
              onClick={() => setStep("preview")}
              disabled={
                !mapping.description ||
                !mapping.date ||
                (!mapping.amount && !mapping.withdrawal && !mapping.deposit)
              }
            >
              Preview Import
            </Button>
          </div>
        </div>
      </AppLayout>
    )
  }

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title="Import Preview" />

      <div className="mx-auto w-full max-w-4xl space-y-6 p-4">
        <div>
          <h2 className="text-lg font-semibold">Import Preview</h2>
          <p className="text-muted-foreground text-sm">
            Step 3 of 3: Review and confirm
          </p>
        </div>

        <div className="flex gap-4">
          <Badge variant="outline" className="gap-1">
            <CheckCircle className="h-3 w-3 text-green-500" />
            {categorizedCount} categorized
          </Badge>
          <Badge variant="outline" className="gap-1">
            <AlertTriangle className="h-3 w-3 text-amber-500" />
            {needsReviewCount} need review
          </Badge>
          {duplicateCount > 0 && (
            <Badge variant="outline" className="gap-1">
              <XCircle className="h-3 w-3 text-orange-500" />
              {duplicateCount} duplicates (will skip)
            </Badge>
          )}
          {parse_errors.length > 0 && (
            <Badge variant="outline" className="gap-1 text-red-500">
              {parse_errors.length} parse errors
            </Badge>
          )}
        </div>

        <div className="space-y-2">
          {rows.map((row) => (
            <div
              key={row.index}
              className={`flex items-center justify-between rounded-lg border px-4 py-3 ${
                row.duplicate ? "opacity-50" : ""
              }`}
            >
              <div className="flex items-center gap-3">
                {row.status === "categorized" ? (
                  <CheckCircle className="h-4 w-4 text-green-500" />
                ) : row.duplicate ? (
                  <XCircle className="h-4 w-4 text-orange-500" />
                ) : (
                  <AlertTriangle className="h-4 w-4 text-amber-500" />
                )}
                <div>
                  <span className="font-medium">{row.description}</span>
                  <span className="text-muted-foreground ml-2 text-sm">
                    {row.date}
                  </span>
                </div>
              </div>
              <div className="flex items-center gap-3">
                {row.category_name ? (
                  <Badge variant="secondary" className="text-xs">
                    {row.category_name}
                  </Badge>
                ) : row.duplicate ? (
                  <Badge variant="outline" className="text-xs text-orange-500">
                    Duplicate
                  </Badge>
                ) : (
                  <Badge variant="outline" className="text-xs text-amber-500">
                    Needs Review
                  </Badge>
                )}
                <span className="w-24 text-right font-mono text-sm font-medium">
                  {formatCurrency(row.amount_cents / 100)}
                </span>
              </div>
            </div>
          ))}
        </div>

        {total_rows > 500 && (
          <p className="text-muted-foreground text-sm">
            Showing first 500 of {total_rows} rows
          </p>
        )}

        <div className="flex items-end gap-3">
          <div className="grid gap-1">
            <Label className="text-xs">Source name (optional)</Label>
            <Input
              value={sourceName}
              onChange={(e) => setSourceName(e.target.value)}
              placeholder="e.g. Chase Business Checking"
              className="w-64"
            />
          </div>
          <div className="ml-auto flex gap-3">
            <Button variant="outline" onClick={() => setStep("mapping")}>
              Back
            </Button>
            <Button onClick={handleConfirm} disabled={submitting}>
              {submitting
                ? "Importing..."
                : `Import ${total_rows - duplicateCount} entries`}
            </Button>
          </div>
        </div>
      </div>
    </AppLayout>
  )
}
